import React from 'react';

import { isEmpty, isPlainObject } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { CustomUnitsDefinition, HandlingUnits } from '@bringg/types';
import { InputNumber } from '@bringg/react-components';
import { InfoTooltip } from '@bringg-frontend/ui';

import { useStores } from '../../../../recipes';
import { MIN_NUMBER_VALUE } from '../constants';

interface Props {
	// "Clear all" resets the field to '' (see resetValues in vehicle-profile-form.tsx)
	value?: HandlingUnits | '';
	onChange?: (value: HandlingUnits) => void;
}

const HandlingUnitsListInput = ({ value, onChange }: Props) => {
	const { t } = useTranslation();
	const { merchantConfigurationsStore } = useStores();
	const customUnitsDefinition: CustomUnitsDefinition =
		merchantConfigurationsStore.configuration?.custom_units_definition ?? {};

	if (isEmpty(customUnitsDefinition)) {
		return null;
	}

	const handlingUnits: HandlingUnits = isPlainObject(value) ? (value as HandlingUnits) : {};

	const handleValueChange = (unitKey: string, newValue: number | null) =>
		onChange?.({ ...handlingUnits, [unitKey]: newValue });

	return (
		<div className="vehicle-profiles-form--flex-group">
			<div className="vehicle-profiles-form--group-title">
				{t('VEHICLE_PROFILES.FORM.HANDLING_UNITS')}
				<InfoTooltip title={t('VEHICLE_PROFILES.FORM.HELP.HANDLING_UNITS_VALUE')} placement="right" />
			</div>
			{Object.keys(customUnitsDefinition).map(unitKey => (
				<div key={unitKey} className="vehicle-profiles-form--input-group">
					<div className="vehicle-profiles-form--label-text">{customUnitsDefinition[unitKey].name}</div>
					<InputNumber
						placeholder={t('VEHICLE_PROFILES.FORM.HANDLING_UNITS_VALUE_PLACEHOLDER')}
						onChange={newValue => handleValueChange(unitKey, newValue as number | null)}
						precision={0}
						value={handlingUnits[unitKey] ?? null}
						className="vehicle-profiles-form--input"
						min={MIN_NUMBER_VALUE}
						data-test-id={`handling-units-${unitKey}`}
					/>
				</div>
			))}
		</div>
	);
};

export default observer(HandlingUnitsListInput);
